import React, { createContext, useContext } from 'react';
import styled, { css, DefaultTheme, StyledComponent } from 'styled-components';

const CardContext = createContext<boolean>(false);

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isParent?: boolean;
  alwaysElevated?: boolean;
  noMargins?: boolean;
  isCompact?: boolean;
  testId?: string;
}

export const Card: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
  children,
  alwaysElevated = false,
  noMargins = false,
  isCompact,
  testId,
  ...rest
}) => {
  const hasParent = useContext(CardContext);

  const content = (
    <Wrapper
      data-test-id={testId}
      hasParent={hasParent}
      alwaysElevated={alwaysElevated}
      noMargins={noMargins}
      isCompact={isCompact}
      className="bg-white"
      {...rest}
    >
      {children}
    </Wrapper>
  );

  if (!hasParent) {
    return <CardContext.Provider value={true}>{content}</CardContext.Provider>;
  }

  return content;
};

const Wrapper: StyledComponent<
  'div',
  DefaultTheme,
  {
    alwaysElevated: boolean;
    hasParent: boolean;
    noMargins?: boolean;
    isCompact?: boolean;
  },
  never
> = styled.div<{
  alwaysElevated: boolean;
  hasParent: boolean;
  noMargins?: boolean;
  isCompact?: boolean;
}>`
  ${(props) =>
    !props.noMargins &&
    css`
      margin-bottom: ${props.theme.spacing.large};

    ${
      props.hasParent &&
      css`
        margin-top: -${props.theme.spacing.large};
        margin-left: -${props.theme.spacing.large};
        margin-right: -${props.theme.spacing.large};
      `
    }}

    ${
      props.hasParent &&
      css`
        ${Wrapper} > &:last-child {
          margin-bottom: -${props.theme.spacing.large};
        }
      `
    }
`}

  padding: ${(props) => (props.isCompact ? props.theme.spacing.small : props.theme.spacing.large)};

  ${(props) =>
    (!props.hasParent || props.alwaysElevated) &&
    css`
      box-shadow: 0 1px 1px 0 rgba(63, 63, 68, 0.1);
    `}
`;
